import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { pcpReviewStatus } from "../lib/roles";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, Clock, CheckCircle2, Mail, Loader2 } from "lucide-react";

export default function RenewalsView({ onOpenConsumer }) {
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  const emailDigest = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/renewals/email");
      toast.success(`Digest emailed to ${data.sent_to} (${data.count} due).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email.");
    } finally {
      setEmailing(false);
    }
  };

  useEffect(() => {
    api
      .get("/consumers")
      .then((r) => setConsumers(r.data))
      .catch(() => toast.error("Could not load consumers."))
      .finally(() => setLoading(false));
  }, []);

  const rows = consumers
    .map((c) => ({ c, r: pcpReviewStatus(c) }))
    .filter((x) => x.r);
  const overdue = rows.filter((x) => x.r.level === "overdue").sort((a, b) => a.r.days - b.r.days);
  const soon = rows.filter((x) => x.r.level === "soon").sort((a, b) => a.r.days - b.r.days);
  const ok = rows.filter((x) => x.r.level === "ok").sort((a, b) => a.r.days - b.r.days);
  const noDate = consumers.filter((c) => !pcpReviewStatus(c));

  const Card = ({ c, r }) => (
    <button
      data-testid={`renewal-row-${c.id}`}
      onClick={() => onOpenConsumer && onOpenConsumer(c)}
      className="w-full flex items-center justify-between gap-3 p-3.5 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#4A6B53] transition-colors text-left"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#121814] truncate">{c.name}</p>
        <p className="text-xs text-[#7A8C82]">{c.member_id || "No record #"} · review {r.date}</p>
      </div>
      <span
        className={`text-[10px] font-bold uppercase px-2 py-1 rounded shrink-0 ${
          r.level === "overdue" ? "bg-[#C26D5C] text-white" : r.level === "soon" ? "bg-[#F6E9C9] text-[#8a6d1f]" : "bg-[#EAE8E3] text-[#4A6B53]"
        }`}
      >
        {r.label}
      </span>
    </button>
  );

  return (
    <div data-testid="renewals-view">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <CalendarClock className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
            PCP Renewals Due
          </h1>
          <p className="text-sm text-[#7A8C82]">Person-Centered Plans must be reviewed at least every 6 months.</p>
        </div>
        <button
          data-testid="email-digest-btn"
          onClick={emailDigest}
          disabled={emailing}
          className="ml-auto flex items-center gap-2 h-10 px-4 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-sm font-medium transition-colors"
        >
          {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
          Email me this digest
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#E2DFD8] rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">Overdue</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-[#C26D5C]" data-testid="renewals-overdue-count">{overdue.length}</p>
        </div>
        <div className="bg-white border border-[#E2DFD8] rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">Due within 30 days</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-[#D4A373]" data-testid="renewals-soon-count">{soon.length}</p>
        </div>
        <div className="bg-white border border-[#E2DFD8] rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">Current</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-[#4A6B53]">{ok.length}</p>
        </div>
      </div>

      {loading && <p className="text-sm text-[#7A8C82]">Loading…</p>}

      {!loading && overdue.length > 0 && (
        <section className="mb-6">
          <p className="text-[10px] font-bold text-[#C26D5C] uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} /> Overdue</p>
          <div className="space-y-2">{overdue.map((x) => <Card key={x.c.id} {...x} />)}</div>
        </section>
      )}
      {!loading && soon.length > 0 && (
        <section className="mb-6">
          <p className="text-[10px] font-bold text-[#8a6d1f] uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={2} /> Due soon</p>
          <div className="space-y-2">{soon.map((x) => <Card key={x.c.id} {...x} />)}</div>
        </section>
      )}
      {!loading && ok.length > 0 && (
        <section className="mb-6">
          <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Current</p>
          <div className="space-y-2">{ok.map((x) => <Card key={x.c.id} {...x} />)}</div>
        </section>
      )}
      {!loading && overdue.length + soon.length + ok.length === 0 && (
        <p className="text-sm text-[#7A8C82]">No PCP review dates set yet. Add a review date on each consumer in the Consumers tab (or it's set automatically when a PCP is created).</p>
      )}
      {!loading && noDate.length > 0 && (
        <p className="text-xs text-[#7A8C82] mt-4">{noDate.length} consumer(s) have no PCP review date set.</p>
      )}
    </div>
  );
}
